/**
 * Fachliche Typen der Notendarstellung.
 *
 * Dieses Modul haengt ausschliesslich vom musikalischen Modell ab –
 * nicht von HTML, DOM oder SVG. Die eigentliche SVG-Ausgabe liegt in
 * `renderSvg.ts` und konsumiert nur das hier/aus `layout.ts` erzeugte,
 * rein geometrische Ergebnis.
 */

import { DrumInstrument } from '../model/types';

/**
 * Notenwert eines Ereignisses. In DP-2 treten im Sechzehntelraster
 * genau diese Werte auf.
 */
export type NoteValue = 'sixteenth' | 'eighth' | 'dotted-eighth' | 'quarter';

/** Notenwert eines Pausenereignisses (keine punktierten Pausen in DP-2). */
export type RestValue = 'sixteenth' | 'eighth' | 'quarter';

/** Dauer eines Notenwerts in Sechzehnteln. */
export const NOTE_VALUE_SIXTEENTHS: Readonly<Record<NoteValue, number>> = {
  sixteenth: 1,
  eighth: 2,
  'dotted-eighth': 3,
  quarter: 4,
};

/** Anzahl der Balken/Fähnchen eines Notenwerts. */
export const NOTE_VALUE_BEAMS: Readonly<Record<NoteValue, number>> = {
  quarter: 0,
  eighth: 1,
  'dotted-eighth': 1,
  sixteenth: 2,
};

/**
 * Stimme im zweistimmigen Schlagzeugsatz.
 * `up`: Hände (Cymbals, Toms, Snare), Hälse nach oben.
 * `down`: Fuß (Bassdrum), Hälse nach unten.
 */
export type VoiceId = 'up' | 'down';

/** Kopfform eines Notenkopfs. */
export type NoteHeadShape = 'normal' | 'cross';

/** Halsrichtung. */
export type StemDirection = 'up' | 'down';

/**
 * Notationszuordnung eines Instruments: Stimme, vertikale Position im
 * System und Kopfform.
 *
 * `staffPosition` zaehlt in halben Zeilenabstaenden ab der untersten
 * Systemlinie (0). Linien liegen auf 0, 2, 4, 6, 8; Zwischenraeume auf
 * 1, 3, 5, 7; Positionen darueber sind > 8.
 */
export interface InstrumentNotation {
  readonly voice: VoiceId;
  readonly staffPosition: number;
  readonly head: NoteHeadShape;
}

export const INSTRUMENT_NOTATION: Readonly<
  Record<DrumInstrument, InstrumentNotation>
> = {
  // Crash: Kreuz-Notenkopf deutlich ueber der obersten Linie.
  [DrumInstrument.Crash]: { voice: 'up', staffPosition: 11, head: 'cross' },
  // Ride: Kreuz-Notenkopf auf der obersten Systemlinie.
  [DrumInstrument.Ride]: { voice: 'up', staffPosition: 8, head: 'cross' },
  // Hi-Hat: Kreuz-Notenkopf im Zwischenraum ueber der obersten Linie.
  [DrumInstrument.HiHat]: { voice: 'up', staffPosition: 9, head: 'cross' },
  // High Tom: oberer Bereich des Systems, vierter Zwischenraum von unten.
  [DrumInstrument.HighTom]: { voice: 'up', staffPosition: 7, head: 'normal' },
  // Mid Tom: vierte Linie von unten, mittig oberhalb der Snare.
  [DrumInstrument.MidTom]: { voice: 'up', staffPosition: 6, head: 'normal' },
  // Snare: dritter Zwischenraum von unten.
  [DrumInstrument.Snare]: { voice: 'up', staffPosition: 5, head: 'normal' },
  // Floor Tom: zweiter Zwischenraum von unten, unterhalb der Snare.
  [DrumInstrument.FloorTom]: { voice: 'up', staffPosition: 3, head: 'normal' },
  // Bassdrum: unterster Zwischenraum, Hals nach unten.
  [DrumInstrument.Bassdrum]: {
    voice: 'down',
    staffPosition: 1,
    head: 'normal',
  },
};

/** Halsrichtung je Stimme. */
export const VOICE_STEM_DIRECTION: Readonly<Record<VoiceId, StemDirection>> = {
  up: 'up',
  down: 'down',
};

/** Alle Stimmen in Renderreihenfolge. */
export const VOICES: readonly VoiceId[] = ['up', 'down'];

/** Anzahl der Zählzeiten (Viertel) pro Takt in 4/4. */
export const BEATS_PER_BAR = 4;

/** Sechzehntel pro Zählzeit. */
export const STEPS_PER_BEAT = 4;
